import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
} from "react";
import type { Identifier } from "ra-core";
import { Columns2, Link2, Link2Off, Rows4 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  buildFramingStageViews,
  type FramingSceneInspector,
  type FramingStageKey,
  type FramingStageView,
} from "../framingInspector";
import { useFramingSceneInspector } from "../useFramingSceneInspector";
import { FramingAttemptComparison } from "./FramingAttemptComparison";
import { FramingAttemptSelector } from "./FramingAttemptSelector";
import { FramingEvidenceState } from "./FramingEvidenceState";
import { FramingOverlayPresetMenu } from "./FramingOverlayPresetMenu";
import { FramingStagePane, buildStagePaneModel } from "./FramingStagePane";
import { FramingSubjectVerdictTable } from "./FramingSubjectVerdictTable";
import { FramingTimeline } from "./FramingTimeline";
import {
  FRAMING_OVERLAY_CLASS_LABELS,
  FRAMING_OVERLAY_PRESETS,
  type FramingOverlayClass,
} from "./framingOverlayPresets";
import {
  validateFramingHints,
  type FramingDeepLinkHints,
  type FramingWorkbenchAction,
  type FramingWorkbenchState,
} from "./framingWorkbenchState";
import {
  linkEligibility,
  projectCameraBetweenSpaces,
  type FramingViewportCamera,
} from "./framingViewportMath";

/** Narrow error boundary: one failed pane cannot take down Echo detail. */
class WorkbenchErrorBoundary extends Component<
  { children: ReactNode; label: string },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          role="alert"
          className="rounded-md border border-destructive/60 p-4 text-sm"
        >
          {this.props.label} failed to render. The rest of the workbench and the
          Echo page remain usable.
        </div>
      );
    }
    return this.props.children;
  }
}

const QUAD_STAGE_PREFERENCE: ReadonlyArray<readonly FramingStageKey[]> = [
  ["working", "raw"],
  ["provider"],
  ["generated", "pre_effect"],
  ["encoded"],
];

function quadStages(stages: FramingStageView[]): FramingStageView[] {
  const picked: FramingStageView[] = [];
  for (const preference of QUAD_STAGE_PREFERENCE) {
    const found = preference
      .map((key) => stages.find((stage) => stage.key === key))
      .find((stage) => stage != null);
    if (found) picked.push(found);
  }
  return picked;
}

export function FramingWorkbenchDialog({
  sessionId,
  state,
  dispatch,
  hints,
  feedbackSceneRelativeSeconds,
}: {
  sessionId: Identifier;
  state: FramingWorkbenchState;
  dispatch: Dispatch<FramingWorkbenchAction>;
  hints?: FramingDeepLinkHints | null;
  feedbackSceneRelativeSeconds?: number | null;
}) {
  const inspectorQuery = useFramingSceneInspector({
    sessionId,
    sceneId: state.sceneId ?? "",
    attemptId: state.attemptId,
    enabled: state.open && state.sceneId != null,
  });
  const inspector = inspectorQuery.data ?? null;
  const refreshedEpochRef = useRef<number>(-1);
  const [advancedClasses, setAdvancedClasses] = useState<FramingOverlayClass[]>(
    [...FRAMING_OVERLAY_PRESETS.subject_safety],
  );

  // One controlled signed-media recovery per selection; never a loop.
  const handleMediaError = useCallback(() => {
    if (refreshedEpochRef.current === state.selectionEpoch) return;
    refreshedEpochRef.current = state.selectionEpoch;
    void inspectorQuery.refetch().then(() => {
      dispatch({ type: "media_refreshed" });
    });
  }, [dispatch, inspectorQuery, state.selectionEpoch]);

  const stages = useMemo(
    () => (inspector ? buildFramingStageViews(inspector) : []),
    [inspector],
  );
  const visibleStages = useMemo(() => {
    if (state.layout === "single") {
      const active =
        stages.find((stage) => stage.key === state.activeStage) ?? stages[0];
      return active ? [active] : [];
    }
    if (state.layout === "all") return stages;
    const quad = quadStages(stages);
    return quad.length > 0 ? quad : stages;
  }, [stages, state.activeStage, state.layout]);

  // Mobile presents a single usable stage instead of four tiny panes.
  useEffect(() => {
    if (!state.open) return;
    const media = window.matchMedia("(max-width: 767px)");
    const apply = () => {
      if (media.matches && state.layout !== "single") {
        dispatch({ type: "set_layout", layout: "single" });
      }
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [dispatch, state.layout, state.open]);

  const hintValidation = useMemo(
    () => (inspector && hints ? validateFramingHints(hints, inspector) : null),
    [hints, inspector],
  );

  const paneCameras = useMemo(() => {
    const cameras = new Map<
      string,
      { camera: FramingViewportCamera | null; reason: string | null }
    >();
    if (!inspector) return cameras;
    for (const stage of visibleStages) {
      const spaceId = stage.coordinateSpace?.id;
      if (!spaceId || !state.camera) {
        cameras.set(stage.key, { camera: null, reason: null });
        continue;
      }
      if (!state.linkViews) {
        cameras.set(stage.key, {
          camera: state.camera.rootId === spaceId ? state.camera : null,
          reason: null,
        });
        continue;
      }
      const projected = projectCameraBetweenSpaces(
        state.camera,
        state.camera.rootId,
        spaceId,
        inspector.coordinateSpaces,
      );
      if (projected) {
        cameras.set(stage.key, { camera: projected, reason: null });
      } else {
        const eligibility = linkEligibility(
          state.camera.rootId,
          spaceId,
          inspector.coordinateSpaces,
        );
        cameras.set(stage.key, {
          camera: state.camera.rootId === spaceId ? state.camera : null,
          reason: eligibility.linked ? null : eligibility.reason,
        });
      }
    }
    return cameras;
  }, [inspector, state.camera, state.linkViews, visibleStages]);

  const anyLinkable = useMemo(() => {
    if (!inspector) return false;
    const spaceIds = visibleStages
      .map((stage) => stage.coordinateSpace?.id)
      .filter((id): id is string => Boolean(id));
    for (let index = 0; index < spaceIds.length; index += 1) {
      for (let other = index + 1; other < spaceIds.length; other += 1) {
        if (
          linkEligibility(
            spaceIds[index],
            spaceIds[other],
            inspector.coordinateSpaces,
          ).linked
        ) {
          return true;
        }
      }
    }
    return false;
  }, [inspector, visibleStages]);

  const setCamera = useCallback(
    (camera: FramingViewportCamera) => {
      dispatch({ type: "set_camera", camera });
    },
    [dispatch],
  );

  if (!state.open || state.sceneId == null) return null;

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) dispatch({ type: "close" });
      }}
    >
      <DialogContent
        className="h-[95vh] w-[98vw] max-w-none overflow-y-auto sm:max-w-none"
        data-testid="framing-workbench"
      >
        <DialogTitle>Crop forensics — scene {state.sceneId}</DialogTitle>
        <DialogDescription className="sr-only">
          Four-stage crop evidence comparison with overlays, subject verdicts,
          timeline, and attempt history.
        </DialogDescription>

        {hintValidation?.status === "mismatch" ? (
          <div
            role="alert"
            className="rounded-md border border-amber-400/70 bg-amber-50 p-2 text-xs dark:bg-amber-950/30"
          >
            {hintValidation.warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </div>
        ) : null}

        {inspectorQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">
            Loading authorized scene evidence…
          </p>
        ) : inspectorQuery.isError ? (
          <FramingEvidenceState
            availability="network_error"
            onRetry={() => void inspectorQuery.refetch()}
          />
        ) : inspector ? (
          <div className="space-y-3">
            <WorkbenchErrorBoundary label="Workbench header">
              <div className="flex flex-wrap items-center gap-2">
                <FramingAttemptSelector
                  inspector={inspector}
                  selectedAttemptId={state.attemptId}
                  onSelect={(attemptId) =>
                    dispatch({ type: "select_attempt", attemptId })
                  }
                />
                <Select
                  value={state.layout}
                  onValueChange={(value) =>
                    dispatch({
                      type: "set_layout",
                      layout: value as FramingWorkbenchState["layout"],
                    })
                  }
                >
                  <SelectTrigger
                    size="sm"
                    className="min-w-36"
                    aria-label="Stage layout"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quad">
                      <Columns2 className="size-3" /> Four stages
                    </SelectItem>
                    <SelectItem value="all">
                      <Rows4 className="size-3" /> All lineage
                    </SelectItem>
                    <SelectItem value="single">Single stage</SelectItem>
                  </SelectContent>
                </Select>
                {state.layout === "single" ? (
                  <Select
                    value={state.activeStage}
                    onValueChange={(value) =>
                      dispatch({
                        type: "set_stage",
                        stage: value as FramingStageKey,
                      })
                    }
                  >
                    <SelectTrigger
                      size="sm"
                      className="min-w-44"
                      aria-label="Stage"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.key} value={stage.key}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                <FramingOverlayPresetMenu
                  preset={state.overlayPreset}
                  onPresetChange={(preset) =>
                    dispatch({ type: "set_preset", preset })
                  }
                  advancedClasses={advancedClasses}
                  onAdvancedClassesChange={setAdvancedClasses}
                />
                <Button
                  size="sm"
                  variant={state.linkViews ? "default" : "outline"}
                  disabled={!anyLinkable}
                  aria-pressed={state.linkViews}
                  title={
                    anyLinkable
                      ? "Synchronize zoom/pan across panes that share a coordinate root"
                      : "No two visible panes share a deterministic coordinate root"
                  }
                  onClick={() =>
                    dispatch({
                      type: "toggle_link_views",
                      enabled: !state.linkViews,
                    })
                  }
                >
                  {state.linkViews ? (
                    <Link2 className="size-3.5" />
                  ) : (
                    <Link2Off className="size-3.5" />
                  )}
                  Link views
                </Button>
                <Button
                  size="sm"
                  variant={state.comparison ? "default" : "outline"}
                  aria-pressed={Boolean(state.comparison)}
                  disabled={inspector.attempts.length < 2}
                  onClick={() =>
                    dispatch({
                      type: "set_comparison",
                      comparison: state.comparison
                        ? null
                        : {
                            attemptB:
                              inspector.attempts.find(
                                (attempt) =>
                                  attempt.attemptId !==
                                  (state.attemptId ??
                                    inspector.attemptId ??
                                    inspector.current.attemptId),
                              )?.attemptId ?? inspector.attempts[0].attemptId,
                            stage: state.activeStage,
                          },
                    })
                  }
                >
                  Compare attempts
                </Button>
                {!anyLinkable ? (
                  <span className="text-[11px] text-muted-foreground">
                    Link views is unavailable: generated pixels form a new
                    coordinate root without a measured mapping.
                  </span>
                ) : null}
              </div>
            </WorkbenchErrorBoundary>

            <WorkbenchErrorBoundary label="Stage comparison">
              <div
                className={
                  state.layout === "single"
                    ? "grid grid-cols-1 gap-2"
                    : "grid grid-cols-1 gap-2 md:grid-cols-2"
                }
              >
                {visibleStages.map((stage) => {
                  const pane = paneCameras.get(stage.key);
                  return (
                    <FramingStagePane
                      key={stage.key}
                      model={buildStagePaneModel(
                        inspector,
                        stage,
                        state.overlayPreset,
                        state.timeSeconds,
                        state.overlayPreset === "advanced"
                          ? advancedClasses
                          : undefined,
                      )}
                      camera={pane?.camera ?? null}
                      onCameraChange={setCamera}
                      timeSeconds={state.timeSeconds}
                      onMediaError={handleMediaError}
                      linkedDisabledReason={
                        state.linkViews ? (pane?.reason ?? null) : null
                      }
                    />
                  );
                })}
              </div>
            </WorkbenchErrorBoundary>

            {state.comparison ? (
              <WorkbenchErrorBoundary label="Attempt comparison">
                <FramingAttemptComparison
                  sessionId={sessionId}
                  inspectorA={inspector}
                  attemptB={state.comparison.attemptB}
                  stage={state.comparison.stage}
                  timeSeconds={state.timeSeconds}
                />
              </WorkbenchErrorBoundary>
            ) : null}

            <WorkbenchErrorBoundary label="Overlay legend">
              <OverlayLegend inspector={inspector} state={state} />
            </WorkbenchErrorBoundary>

            <WorkbenchErrorBoundary label="Subject verdicts">
              <FramingSubjectVerdictTable inspector={inspector} />
            </WorkbenchErrorBoundary>

            <WorkbenchErrorBoundary label="Timeline">
              <FramingTimeline
                inspector={inspector}
                timeSeconds={state.timeSeconds}
                onTimeChange={(timeSeconds) =>
                  dispatch({ type: "set_time", timeSeconds })
                }
                feedbackSceneRelativeSeconds={feedbackSceneRelativeSeconds}
              />
            </WorkbenchErrorBoundary>

            <footer className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <Badge variant="outline">
                plan {inspector.current.planDigest?.slice(0, 12) ?? "unknown"}
              </Badge>
              <Badge variant="outline">
                attempt {inspector.attemptId ?? "legacy"}
              </Badge>
              {inspector.current.configReleaseId ? (
                <Badge variant="outline">
                  config {inspector.current.configReleaseId}
                </Badge>
              ) : null}
              {inspector.historyTruncated ? (
                <Badge variant="outline">history truncated</Badge>
              ) : null}
              <span>
                Media links are short-lived; this access is recorded in the
                evidence audit.
              </span>
            </footer>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function OverlayLegend({
  inspector,
  state,
}: {
  inspector: FramingSceneInspector;
  state: FramingWorkbenchState;
}) {
  const classes =
    state.overlayPreset === "advanced"
      ? FRAMING_OVERLAY_PRESETS.advanced
      : FRAMING_OVERLAY_PRESETS[state.overlayPreset];
  if (classes.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Overlays are off; pixels only.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]">
      <span className="text-muted-foreground">
        Overlay legend ({inspector.evidenceMode} evidence):
      </span>
      {classes.map((overlayClass) => (
        <Badge key={overlayClass} variant="outline">
          {FRAMING_OVERLAY_CLASS_LABELS[overlayClass]}
        </Badge>
      ))}
    </div>
  );
}
